/**
 * Tombstone - Content deletion marker
 *
 * A tombstone marks content as deleted. Only the original creator
 * can create a tombstone for their own content.
 */

import type { DAGObject, ObjectType } from '../dag/types.js';

/**
 * Tombstone content structure
 */
export interface TombstoneContent {
  /** Hash of the object being deleted */
  targetHash: string;
  /** Optional reason for deletion */
  reason?: string;
}

/**
 * Type guard for tombstone objects
 */
export function isTombstone(obj: DAGObject): obj is DAGObject<TombstoneContent> {
  return obj.type === 'tombstone';
}

/**
 * Create a tombstone object content
 *
 * Note: The caller must ensure they are the creator of the target object
 * before creating a tombstone. The DAGStore will validate this.
 *
 * @param targetHash - Hash of the object to mark as deleted
 * @param reason - Optional reason for deletion
 * @returns Tombstone content (to be signed by identity)
 */
export function createTombstoneContent(
  targetHash: string,
  reason?: string
): { type: ObjectType; content: TombstoneContent } {
  return {
    type: 'tombstone' as ObjectType,
    content: {
      targetHash,
      reason,
    },
  };
}

/**
 * Get the target hash from a tombstone
 */
export function getTombstoneTarget(tombstone: DAGObject<TombstoneContent>): string {
  return tombstone.content.targetHash;
}
